/**
 * WUI-408: хелперы для Tiles `<inlineAfterLoad>` / `setInlineVisibility`.
 */
import type { ScriptData } from '@/components/platform/types'
import { getWebClientExecFromScriptNode } from '@/common/utils/webClientExec'
import {
  buildTilesRowRegistryPrefix,
  type SwingTilesRowScopeLike,
} from '@/common/utils/tilesRowPanelData'
import { resolveTilesRowScopedRegistryName } from '@/common/utils/resolveTilesRowScopedRegistryName'

/** Узел скрипта + тело для маршрутизации (клиент vs middle). */
export type TilesInlineAfterLoadScript = {
  text: string
  /** True только если в XML есть web_client_exec="true". */
  webClientExec: boolean
  scriptData?: ScriptData
}

/** Разбирает узел/строку `inlineAfterLoad` из XML (текст + web_client_exec). */
export function resolveTilesInlineAfterLoadScript(
  node: unknown,
): TilesInlineAfterLoadScript {
  if (node == null) {
    return { text: '', webClientExec: false }
  }
  if (typeof node === 'string') {
    return { text: node.trim(), webClientExec: false }
  }
  if (typeof node !== 'object') {
    return { text: '', webClientExec: false }
  }
  const scriptData = node as ScriptData
  const text = String(scriptData._text ?? (scriptData as { _cdata?: unknown })._cdata ?? '').trim()
  return {
    text,
    webClientExec: getWebClientExecFromScriptNode(scriptData),
    scriptData,
  }
}

/** Нужно ли выполнять inlineAfterLoad через en.run на клиенте (явный web_client_exec=true). */
export function shouldRunTilesInlineAfterLoadOnClient(
  script: TilesInlineAfterLoadScript,
): boolean {
  return Boolean(script.text) && script.webClientExec === true
}

/** Имена element/action из вызовов setInlineVisibility("…") в тексте inlineAfterLoad. */
export function parseTilesInlineVisibilityTargets(scriptText: string): ReadonlySet<string> {
  const names = new Set<string>()
  const text = String(scriptText ?? '')
  const re = /setInlineVisibility\s*\(\s*(["'])([^"']+)\1/g
  for (let match = re.exec(text); match; match = re.exec(text)) {
    names.add(match[2])
  }
  return names
}

/** Поля строки из getFieldAsString("…") в inlineAfterLoad (исключая putFullData page fields). */
export function parseInlineAfterLoadRowFieldNames(scriptText: string): string[] {
  const pageFields = new Set(parseInlineAfterLoadPutFullDataFieldNames(scriptText))
  const names = new Set<string>()
  const text = String(scriptText ?? '')
  const re = /getFieldAsString\s*\(\s*(["'])([^"']+)\1/g
  for (let match = re.exec(text); match; match = re.exec(text)) {
    const name = match[2]
    if (!pageFields.has(name)) {
      names.add(name)
    }
  }
  return [...names]
}

/** Поля формы из putFullData("…") в inlineAfterLoad (orderStatusId и т.п.). */
export function parseInlineAfterLoadPutFullDataFieldNames(scriptText: string): string[] {
  const names = new Set<string>()
  const text = String(scriptText ?? '')
  const re = /putFullData\s*\(\s*(["'])([^"']+)\1/g
  for (let match = re.exec(text); match; match = re.exec(text)) {
    names.add(match[2])
  }
  return [...names]
}

function normalizeInlineAfterLoadSignatureValue(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'object' && value !== null && 'value' in (value as object)) {
    return String((value as { value: unknown }).value ?? '')
  }
  return String(value)
}

/** Стабильный ключ документа строки tiles (_id + rowIndex). */
export function buildTilesRowDocumentKey(row: unknown, rowIndex: number): string {
  const r = row as Record<string, unknown> | undefined
  const idPart =
    (r?.fields as { link?: { _id?: { value?: unknown } } })?.link?._id?.value ??
    (r?.fields as { document?: { _id?: { value?: unknown } } })?.document?._id?.value ??
    (r?.fields as { _id?: { value?: unknown } })?._id?.value ??
    rowIndex
  return `tile-${rowIndex}-${String(idPart)}`
}

export type BuildTilesInlineAfterLoadRowsSignatureInput = {
  rows: unknown[]
  scriptText: string
  readRowField: (row: unknown, fieldName: string) => unknown
}

/** Сигнатура одной строки: document key + row fields из getFieldAsString (без page putFullData). */
export function buildTilesInlineAfterLoadRowSignature(
  row: unknown,
  rowIndex: number,
  scriptText: string,
  readRowField: (row: unknown, fieldName: string) => unknown,
): string {
  const rowFieldNames = parseInlineAfterLoadRowFieldNames(scriptText)
  const base = buildTilesRowDocumentKey(row, rowIndex)
  if (rowFieldNames.length === 0) return base
  const fieldParts = rowFieldNames
    .map((name) => `${name}=${normalizeInlineAfterLoadSignatureValue(readRowField(row, name))}`)
    .join(',')
  return `${base}|${fieldParts}`
}

/**
 * Сигнатура для re-run inlineAfterLoad: только row document key + поля строки.
 * Page putFullData fields намеренно не входят — иначе setPageData триггерит premature run.
 */
export function buildTilesInlineAfterLoadRowsSignature(
  input: BuildTilesInlineAfterLoadRowsSignatureInput,
): string {
  return input.rows
    .map((row, rowIndex) =>
      buildTilesInlineAfterLoadRowSignature(row, rowIndex, input.scriptText, input.readRowField),
    )
    .join('|')
}

/**
 * Сигнатура данных строки для afterRowMount: document key + все колонки строки.
 * Без фиксированных имён полей — любой reload-контент (HTML карточки, status) меняет ключ.
 */
export function buildTilesRowDataChangeSignature(row: unknown, rowIndex: number): string {
  const base = buildTilesRowDocumentKey(row, rowIndex)
  const fields = buildTilesInlineRowSelectedData(row).fields
  const keys = Object.keys(fields).sort()
  if (keys.length === 0) return base
  const parts = keys.map(
    (key) => `${key}=${normalizeInlineAfterLoadSignatureValue(fields[key])}`,
  )
  return `${base}|${parts.join(',')}`
}

/** После reload/panel — все строки затронутой таблицы, не только signature-diff. */
export function listTilesRowIndexesForReloadInlineAfterLoad(rowCount: number): number[] {
  if (!Number.isFinite(rowCount) || rowCount <= 0) return []
  return Array.from({ length: Math.floor(rowCount) }, (_, i) => i)
}

/** Индексы строк, у которых сегмент signature изменился (для точечного re-run). */
export function collectChangedInlineAfterLoadRowIndexes(
  prevByIndex: Record<number, string>,
  nextRows: unknown[],
  scriptText: string,
  readRowField: (row: unknown, fieldName: string) => unknown,
): { changedIndexes: number[]; nextByIndex: Record<number, string> } {
  const nextByIndex: Record<number, string> = {}
  const changedIndexes: number[] = []
  for (let rowIndex = 0; rowIndex < nextRows.length; rowIndex += 1) {
    const next = buildTilesInlineAfterLoadRowSignature(
      nextRows[rowIndex],
      rowIndex,
      scriptText,
      readRowField,
    )
    nextByIndex[rowIndex] = next
    if (prevByIndex[rowIndex] !== next) {
      changedIndexes.push(rowIndex)
    }
  }
  return { changedIndexes, nextByIndex }
}

const TEMP_TILES_INLINE_VISIBILITY_KEY_PREFIX = '__tilesInlineVis:'

export function isTilesElementWithInlineVisibilityRegistered(
  elements: Record<string, { elementId?: number | string; setInlineVisibility?: unknown }> | null | undefined,
  tilesElementId: number | string,
): boolean {
  const id = Number(tilesElementId)
  if (!Number.isFinite(id) || id <= 0) return true
  if (!elements) return false
  return Object.values(elements).some(
    (el) =>
      el != null &&
      Number(el.elementId) === id &&
      typeof el.setInlineVisibility === 'function',
  )
}

/**
 * Child afterRowMount runs before parent ElementRenderer registerElement.
 * Ensure en.getElement(tilesId).setInlineVisibility works for inlineAfterLoad.
 * Returns cleanup fn if a temporary registry entry was added.
 */
export function ensureTilesInlineVisibilityForInlineAfterLoad(
  elements: Record<string, Record<string, unknown>> | null | undefined,
  tilesElementId: number | string,
  setInlineVisibility: (a: boolean | string, b?: boolean) => void,
): (() => void) | null {
  if (!elements || typeof setInlineVisibility !== 'function') return null
  const id = Number(tilesElementId)
  if (!Number.isFinite(id) || id <= 0) return null
  if (isTilesElementWithInlineVisibilityRegistered(elements, id)) return null

  const key = `${TEMP_TILES_INLINE_VISIBILITY_KEY_PREFIX}${id}`
  elements[key] = {
    elementId: id,
    setInlineVisibility,
  }
  return () => {
    if (elements[key]?.setInlineVisibility === setInlineVisibility) {
      delete elements[key]
    }
  }
}

/** Готовит selectedData строки для En/runCode: `{ fields }` из UniDataTable-row. */
export function buildTilesInlineRowSelectedData(row: unknown): { fields: Record<string, unknown> } {
  const rowObj =
    row && typeof row === 'object' && !Array.isArray(row)
      ? (row as Record<string, unknown>)
      : {}
  if (rowObj.fields && typeof rowObj.fields === 'object' && !Array.isArray(rowObj.fields)) {
    return { fields: rowObj.fields as Record<string, unknown> }
  }
  return { fields: rowObj }
}

/**
 * Snapshot формы (statusId заказа `draft`) + поля строки tiles.
 * `_setActionData` при tilesRowScope кладёт только PK — без этого overlay
 * inlineAfterLoad 2573 читает statusId заказа и показывает «Просмотр» вместо «Заполнить».
 */
export function overlayTilesInlineAfterLoadRowFields(
  pageFields: Record<string, unknown>,
  rowSelected: unknown,
): Record<string, unknown> {
  const rowFields = buildTilesInlineRowSelectedData(rowSelected).fields
  return { ...pageFields, ...rowFields }
}

/**
 * Плоские typed-поля для currentData middle executeScript:
 * поля строки + `{table}__rowNum`.
 */
export function buildTilesInlineAfterLoadCurrentDataFields(
  rowFields: Record<string, unknown>,
  tableFieldName: string,
  rowIndex: number,
): Record<string, unknown> {
  const table = String(tableFieldName ?? '').trim()
  const out: Record<string, unknown> = { ...rowFields }
  if (table) {
    out[`${table}__rowNum`] = { type: 'Long', value: rowIndex }
  }
  return out
}

/**
 * Ремап `element_name` в states ответа middle → registry-имя строки tiles
 * (`{table}__rowN__{element}`), чтобы видимость применялась только к текущему клону.
 */
export function remapExchangeStatesForTilesRow(
  states: unknown[] | null | undefined,
  scope: SwingTilesRowScopeLike | null | undefined,
): any[] {
  const list = Array.isArray(states) ? states : []
  if (!scope) return list.map((s) => (s && typeof s === 'object' ? { ...s } : s))

  const prefix = buildTilesRowRegistryPrefix(scope.tableFieldName, scope.rowIndex)
  return list.map((raw) => {
    if (!raw || typeof raw !== 'object') return raw
    const state = { ...(raw as Record<string, unknown>) }
    const name = String(state.element_name ?? '').trim()
    if (!name) return state
    if (prefix && name.startsWith(prefix)) return state
    state.element_name = resolveTilesRowScopedRegistryName(scope, name)
    return state
  })
}

/**
 * Применяет remapped `is_visible` через formProvider.setVisible со scope
 * (formId/panelId). Scope нужен, чтобы не раздавать false на всю панель
 * (см. setVisibleRecursive без propagate при scope).
 */
export function applyTilesRowVisibilityStates(
  states: unknown[] | null | undefined,
  setVisible: ((name: string, visible: boolean, scope?: { formId?: string; panelId?: string }) => void) | undefined,
  options?: { formId?: string; panelId?: string | number },
): void {
  if (!setVisible) return
  const formId = options?.formId != null ? String(options.formId) : undefined
  const panelId = options?.panelId != null ? String(options.panelId) : undefined
  const scope = formId || panelId ? { formId, panelId } : undefined

  for (const raw of Array.isArray(states) ? states : []) {
    if (!raw || typeof raw !== 'object') continue
    const state = raw as Record<string, unknown>
    const name = String(state.element_name ?? '').trim()
    if (!name) continue
    if (state.is_visible === undefined) continue
    const visible =
      typeof state.is_visible === 'boolean'
        ? state.is_visible
        : String(state.is_visible).trim().toLowerCase() === 'true' ||
          String(state.is_visible).trim() === '1'
    setVisible(name, visible, scope)
  }
}
